import React, { useMemo, useState } from "react";

import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { IconButton } from "@mui/material";
import Collapse from "@mui/material/Collapse";

import Visibility from "@src/assets/svg/components/Visibility";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import Conditional from "@src/sharedComponents/Conditional/Conditional";
import { DropdownMenuOption } from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";
import BaseDropdown from "@src/sharedComponents/Dropdown/BaseDropdown";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import TooltipBasic from "@src/sharedComponents/Tooltip/TooltipBasic";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./TableRow.module.css";

type ControlType = "dropdown" | "fixedText" | "none";

interface TableRowProps<T extends string = string> {
  label: string;
  value?: string;
  customValueNode?: React.ReactNode;
  controlType?: ControlType;
  dropdownOptions?: DropdownMenuOption<T>[];
  selectedDropdownOption?: DropdownMenuOption<T>;
  onDropdownSelect?: (option: DropdownMenuOption<T>) => void;
  fixedText?: string;
  tooltipText?: string;
  className?: string;
  hideBottomBorder?: boolean;
  expandableContent?: string;
  dropdownButtonClassName?: string;
  dropdownButtonTextClassName?: string;
  dropdownButtonIconClassName?: string;
  dropdownButtonWidth?: number;
  dropdownMenuWidth?: number;
  isLoading?: boolean;
  loadingWidth?: number;
  loadingHeight?: number;
  isVisible?: boolean;
  isAdvisor?: boolean;
  onToggleVisibility?: () => void;
  disableVisibilityToggle?: boolean;
}

const TableRow = <T extends string = string>({
  label,
  value,
  customValueNode,
  controlType = "none",
  dropdownOptions = [],
  selectedDropdownOption,
  onDropdownSelect,
  fixedText,
  tooltipText,
  className,
  hideBottomBorder,
  expandableContent,
  dropdownButtonClassName = styles.dropdownBtn,
  dropdownButtonTextClassName = styles.dropdownBtnText,
  dropdownButtonIconClassName = styles.dropdownBtnIcon,
  dropdownButtonWidth = 60,
  dropdownMenuWidth = 100,
  isLoading = false,
  loadingWidth = 60,
  loadingHeight = 18,
  isVisible = true,
  isAdvisor = false,
  onToggleVisibility,
  disableVisibilityToggle = false,
}: TableRowProps<T>): React.ReactElement | null => {
  const [expanded, setExpanded] = useState<boolean>(false);

  const textColor = useMemo(() => {
    if (!isVisible && isAdvisor) {
      return "textDisabled";
    }
    return "text";
  }, [isVisible, isAdvisor]);

  if (!isVisible && !isAdvisor) {
    return null;
  }

  // Render value section based on loading state and provided props
  const renderValue = (): React.ReactNode => {
    if (isLoading) {
      return (
        <Skeleton
          width={loadingWidth}
          height={loadingHeight}
          style={{ transform: "none" }}
        />
      );
    }

    if (customValueNode) {
      return customValueNode;
    }

    if (value) {
      return (
        <Text variant="body" color={textColor}>
          {value}
        </Text>
      );
    }

    return null;
  };

  return (
    <div
      className={toClassName(styles.container, className, {
        [styles.noBottomBorder]: hideBottomBorder,
        [styles.disabledRow]: isVisible && isAdvisor,
        [styles.expanded]: expanded,
      })}
    >
      <div className={styles.innerContainer}>
        <div className={styles.flex}>
          {expandableContent && (
            <ArrowRightIcon
              className={toClassName(styles.arrow, {
                [styles.arrowRotateDown]: expanded,
                [styles.arrowRotateRight]: !expanded,
              })}
              onClick={() => setExpanded(!expanded)}
            />
          )}

          <Text variant="body" color={textColor}>
            {label}
          </Text>

          {tooltipText && (
            <TooltipBasic className={styles.tooltip} text={tooltipText} />
          )}

          <Conditional
            condition={
              controlType === "dropdown" &&
              selectedDropdownOption !== undefined &&
              onDropdownSelect !== undefined
            }
          >
            {selectedDropdownOption && onDropdownSelect && (
              <BaseDropdown
                customDropdownBaseClassName={dropdownButtonClassName}
                customDropdownBaseTextClassName={dropdownButtonTextClassName}
                customDropdownBaseIconClassName={dropdownButtonIconClassName}
                curItemLabel={selectedDropdownOption.label}
                options={dropdownOptions}
                onSelect={onDropdownSelect}
                customDropdownBaseWidth={dropdownButtonWidth}
                width={dropdownMenuWidth}
              />
            )}
          </Conditional>
          <Conditional condition={controlType === "fixedText"}>
            <Text
              color={textColor}
              variant="bodySmall"
              className={styles.subText}
            >
              {fixedText}
            </Text>
          </Conditional>
        </div>

        <div className={styles.flex}>
          {renderValue()}

          <Conditional
            condition={isAdvisor && onToggleVisibility !== undefined}
          >
            {onToggleVisibility && (
              <Tooltip
                tooltipText={
                  isVisible
                    ? null
                    : "This stat will be hidden from your client."
                }
              >
                <IconButton
                  className={styles.visibilityBtn}
                  onClick={onToggleVisibility}
                  disabled={disableVisibilityToggle}
                >
                  <Visibility on={isVisible} />
                </IconButton>
              </Tooltip>
            )}
          </Conditional>
        </div>
      </div>

      {expandableContent && (
        <Collapse orientation="vertical" in={expanded} collapsedSize={0}>
          <Text variant="body" color="textSubtle">
            {expandableContent}
          </Text>
        </Collapse>
      )}
    </div>
  );
};

export default TableRow;
